import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import XFrameBypassIframe from '../XFrameBypassIframe';
import memorialService from '../../services/memorialService';
import CandleButton from '../CandleButton';
import StoneButton from '../StoneButton';
import './MemorialModal.css';

/**
 * Modal component for displaying detailed memorial information
 * @param {Object} props - Component props
 * @param {Object} props.memorial - The memorial data to display
 * @param {Function} props.onClose - Function to call when closing the modal
 * @returns {JSX.Element} Rendered MemorialModal component
 */
const MemorialModal = ({ memorial, onClose, onCandleLit, onStonePlaced }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [shareTooltip, setShareTooltip] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Cache animation timers
  const animationTimersRef = useRef({
    closing: null
  });
  
  // Clean up any running animations on unmount
  useEffect(() => {
    return () => {
      if (animationTimersRef.current.closing) {
        clearTimeout(animationTimersRef.current.closing);
      }
    };
  }, []);
  
  // Optimized close handler with animation performance improvements
  const handleClose = useCallback(() => {
    // Start closing animation
    setIsClosing(true);
    
    // Cancel any previous closing animation
    if (animationTimersRef.current.closing) {
      clearTimeout(animationTimersRef.current.closing);
    }
    
    // Use RAF to ensure animations run smoothly
    requestAnimationFrame(() => {
      // Schedule the actual close after animation
      animationTimersRef.current.closing = setTimeout(() => {
        onClose();
        animationTimersRef.current.closing = null;
      }, 300); // Match animation duration
    });
  }, [onClose]);

  const handleCloseClick = useCallback((e) => {
    e.stopPropagation();
    handleClose();
  }, [handleClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);
  
  const handleScroll = useCallback(() => {
    if (modalRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = modalRef.current;
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(progress);
    }
  }, []);
  
  // Touch events for swipe gestures in modal
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartX.current || !touchStartY.current) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;
    
    // Reset touch values
    touchStartX.current = null;
    touchStartY.current = null;
    
    // Horizontal swipe detection (for future: navigate between memorials)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      // Reserved for future: navigate between memorials
      // Left swipe: diffX > 0
      // Right swipe: diffX < 0
    } 
    // Vertical swipe detection (close modal on swipe down)
    else if (Math.abs(diffY) > Math.abs(diffX) && diffY < -80) {
      // Swipe down - close modal
      handleClose();
      
      // Try to trigger haptic feedback if available (iOS/Android)
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(20); // Short vibration
      }
    }
  }, [handleClose]);
  
  // Setup event listeners for scroll and touch
  useEffect(() => {
    const modalElement = modalRef.current;
    if (modalElement) {
      modalElement.addEventListener('scroll', handleScroll);
      
      // Add touch events for swipe gestures
      modalElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      modalElement.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        modalElement.removeEventListener('scroll', handleScroll);
        modalElement.removeEventListener('touchstart', handleTouchStart);
        modalElement.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [handleScroll, handleTouchStart, handleTouchEnd]);
  
  // Focus trap for accessibility - keep focus inside modal when open
  useEffect(() => {
    // Focus the close button when modal opens
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
    
    // Trap focus within modal for keyboard users
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      
      // Get all focusable elements in modal
      const modalElement = modalRef.current;
      if (!modalElement) return;
      
      const focusableElements = modalElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      // If shift+tab and on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } 
      // If tab and on last element, move to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    document.addEventListener('keydown', handleTabKey);
    
    // Disable body scroll when modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.body.style.overflow = originalStyle;
    };
  }, []);
  
  // Handle sharing memorial information
  const handleShare = useCallback(() => {
    // Create a shareable link with memorial ID
    const shareUrl = `${window.location.origin}${window.location.pathname}?memorial=${memorial.id}`;
    const shareTitle = `Memorial for ${memorial.name}`;
    const shareText = `View the memorial for ${memorial.name} on the October 7th Memorial Wall`;
    
    // Try to use the Web Share API if available
    if (navigator.share) {
      navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      }).catch(err => {
        console.log('Share failed:', err);
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareUrl).then(() => {
        // Show copied tooltip
        setShareTooltip(true);
        setTimeout(() => {
          setShareTooltip(false);
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }, [memorial]);
  
  // Memoize special resources to avoid recalculating on every render
  const isFullMemorial = useMemo(() => 
    memorial ? memorialService.isSpecialMemorial(memorial) : false
  , [memorial]);
  
  const fullMemorialUrl = useMemo(() => 
    memorialService.getFullMemorialUrl()
  , []);
  
  const novaFestivalVideo = useMemo(() => 
    memorialService.getNovaFestivalVideo()
  , []);

  // Remove image loading effect since images are already loaded
  useEffect(() => {
    // Images are already loaded, no need for loading indicators
    setImagesLoaded(true);
  }, []);
  
  // Early return after hooks
  if (!memorial) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose} 
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="memorial-modal-title"
      aria-describedby="memorial-modal-content"
    >
      {/* Scroll progress indicator */}
      <div 
        className="modal-progress" 
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />
      
      <div 
        ref={modalRef}
        className={`info-modal ${isFullMemorial ? 'full-memorial' : ''} ${isClosing ? 'closing' : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-container">
          <h2 id="memorial-modal-title">{memorial.name}</h2>

          <button
            ref={closeButtonRef}
            className="close-button"
            onClick={handleCloseClick}
            aria-label="Close memorial information"
          >
            ×
          </button>

          {/* Stone button - leave a stone */}
          <div className="stone-button-wrapper">
            <StoneButton
              memorialId={memorial.id}
              memorialName={memorial.name}
              onPlaced={onStonePlaced}
            />
          </div>

          {/* Candle button - light a yahrzeit candle */}
          <div className="candle-button-wrapper">
            <CandleButton
              memorialId={memorial.id}
              memorialName={memorial.name}
              onLit={onCandleLit}
            />
          </div>

          {/* Share button */}
          <div className="share-button-container">
            <button
              className="share-button"
              onClick={handleShare}
              aria-label="Share this memorial"
            >
              <span className="share-icon">↗</span>
              <span className="share-text">Share</span>
            </button>

            {/* Tooltip for copy success */}
            {shareTooltip && (
              <div className="share-tooltip">
                Link copied to clipboard
              </div>
            )}
          </div>
        </div>
        <div id="memorial-modal-content" className="markdown-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              img: ({node, ...props}) => {
                // Handle video content
                if (props.src && props.src.endsWith('.mp4')) {
                  return (
                    <video 
                      style={{maxWidth: '100%', height: 'auto'}} 
                      controls
                      preload="metadata"
                      poster={props.alt ? `../../images/${props.alt.replace(/\s+/g, '_').toLowerCase()}_poster.jpg` : undefined}
                    >
                      <source src={novaFestivalVideo} type="video/mp4" />
                      <track kind="captions" src="" label="English" />
                      Your browser does not support the video tag.
                    </video>
                  );
                }
                // Handle full memorial with description and button
                if (props.src === fullMemorialUrl) {
                  return (
                    <div className="full-memorial-button-container" style={{margin: '20px 0', textAlign: 'center'}}>
                      <p style={{marginBottom: '15px', lineHeight: '1.5', fontStyle: 'italic'}}>
                        The October 7th Memorial is a digital sanctuary preserving the stories of those whose lives were tragically cut short. Here you'll find personal accounts that honor each individual's unique journey - their dreams, passions, and the moments that defined them. Beyond remembrance, this memorial serves as a testament to lives lived with purpose, ensuring that though they are gone, their light continues to shine in our hearts. May their memory be a blessing.
                      </p>
                      <a 
                        href={fullMemorialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="full-memorial-button"
                        style={{
                          display: 'inline-block',
                          padding: '12px 24px',
                          backgroundColor: '#333',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          margin: '10px 0'
                        }}
                      >
                        Open Full Memorial in New Tab
                      </a>
                    </div>
                  );
                }
                // Regular image with eager loading for Safari compatibility
                return (
                  <img 
                    style={{
                      maxWidth: '100%', 
                      height: 'auto',
                      // Fix specifically for Safari scrolling issues
                      WebkitTransform: 'translateZ(0)',
                      transform: 'translateZ(0)',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden',
                      // Prevent Safari from repainting during scroll
                      willChange: 'auto'
                    }} 
                    alt={props.alt || 'Memorial image'} 
                    loading="eager" // Change to eager loading for Safari
                    src={props.src}
                    width={props.width}
                    height={props.height}
                    className="memorial-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.classList.add('image-error');
                      e.target.alt = 'Image could not be loaded';
                    }}
                  />
                );
              },
              a: ({node, children, ...props}) => (
                <a 
                  {...props} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {children}
                </a>
              )
            }}
          >
            {memorial.info}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

MemorialModal.propTypes = {
  memorial: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    position: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired
    }).isRequired,
    info: PropTypes.string.isRequired
  }),
  onClose: PropTypes.func.isRequired,
  onCandleLit: PropTypes.func,
  onStonePlaced: PropTypes.func,
};

// Wrap with React.memo for performance optimization
export default React.memo(MemorialModal);